import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { descricaoRecurso, motivoIndisponivel, type PerfilEvento } from '@/lib/perfilEvento';
import TribosConfig from '@/pages/gerenciar/direcao/components/TribosConfig';

interface Props {
  eventoId: number;
  perfil: PerfilEvento | null;
  aoMudouRecurso: () => void;
}

const CARD = 'rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]';

// Os interruptores de "Mais recursos" (inclui "Este evento vende produtos").
export default function BlocoRecursos({ eventoId, perfil, aoMudouRecurso }: Props) {
  const [trocando, setTrocando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  async function alternar(chave: string, ligar: boolean) {
    setTrocando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('definir_recurso', {
        p_evento: eventoId,
        p_recurso: chave,
        p_ligar: ligar,
      });
      if (error) throw error;
      setToast(ligar ? 'Recurso ligado.' : 'Recurso desligado.');
      aoMudouRecurso();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setTrocando(false);
    }
  }

  const recursos = perfil?.recursos ?? [];
  const chaves = perfil?.chaves;

  return (
    <div className="space-y-3">
      {erro && (
        <div className="rounded-2xl bg-danger-50 px-4 py-3 text-[14px] text-danger-700">{erro}</div>
      )}

      <div className={CARD}>
        <p className="text-[13px] text-foreground-500">
          Ligue o que este evento precisa — a aba aparece na hora. É aqui também que fica a chave
          "Este evento vende produtos".
        </p>
        <div className="mt-2">
          {recursos.map((r) => {
            const motivo = r.disponivel
              ? null
              : chaves
                ? motivoIndisponivel(r, chaves)
                : 'Não disponível para este tipo de evento.';
            return (
              <div
                key={r.chave}
                className="flex min-h-[60px] items-center justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <p
                    className={`text-[15px] font-medium ${
                      r.disponivel ? 'text-foreground-900' : 'text-foreground-400'
                    }`}
                  >
                    {r.chave === 'loja' ? 'Este evento vende produtos' : r.rotulo}
                  </p>
                  <p className="mt-0.5 text-[13px] text-foreground-500">
                    {motivo ?? descricaoRecurso(r.descricao)}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={r.ligado}
                  aria-label={r.rotulo}
                  disabled={!r.disponivel || trocando}
                  onClick={() => alternar(r.chave, !r.ligado)}
                  className={`relative h-7 w-12 shrink-0 cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    r.ligado ? 'bg-primary-500' : 'bg-secondary-300'
                  }`}
                >
                  <span
                    className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${
                      r.ligado ? 'left-6' : 'left-1'
                    }`}
                  />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tribos do acampamento — some sozinho em evento que não usa tribos. */}
      <TribosConfig eventoId={eventoId} />

      {toast && (
        <div className="rounded-2xl bg-foreground-950 px-4 py-3 text-[14px] text-white">{toast}</div>
      )}
    </div>
  );
}